import { ArrowLink, CardCategory, Col } from "@/components";
import { Section, SectionHeading } from "@/components/ui/Section";
import clsx from "clsx";
import { omit } from "lodash";
import { ComponentType, ReactNode, useMemo } from "react";
import { RichText } from "../RichText";
import "./Services.scss";

export const SERVICES_LAYOUTS = {
  LayoutOne: "LayoutOne",
  LayoutTwo: "LayoutTwo",
  LayoutThree: "LayoutThree",
  LayoutFour: "LayoutFour",
  LayoutFive: "LayoutFive",
} as const;

type ServicesLayout = (typeof SERVICES_LAYOUTS)[keyof typeof SERVICES_LAYOUTS];

export type ServicesProps = {
  title: ReactNode;
  description?: ReactNode;
  goToLabel?: string;
  href?: string;
  asLink?: "a" | ComponentType<any>;
  items: any[];
  layout?: ServicesLayout;
  className?: string;
};

export const Services = ({
  title,
  description,
  goToLabel,
  href,
  asLink = "a",
  items,
  layout = SERVICES_LAYOUTS.LayoutOne,
  className,
}: ServicesProps) => {
  const getLayoutClassName = (l: ServicesLayout) => {
    switch (l) {
      case SERVICES_LAYOUTS.LayoutOne:
        return "ui-service-items-layout-one";
      case SERVICES_LAYOUTS.LayoutTwo:
        return "ui-service-items-layout-two";
      case SERVICES_LAYOUTS.LayoutThree:
        return "ui-service-items-layout-three";
      case SERVICES_LAYOUTS.LayoutFour:
        return "ui-service-items-layout-four";
      case SERVICES_LAYOUTS.LayoutFive:
        return "ui-service-items-layout-five";
      default:
        return "";
    }
  };

  const layoutClassName = getLayoutClassName(layout);

  const itemsNormalized = useMemo(() => {
    switch (layout) {
      /**
       * In this layout, we remove the image from all items.
       */
      case SERVICES_LAYOUTS.LayoutOne:
        return items.map((item) => {
          return {
            ...omit(item, ["image"]),
          };
        });
      /**
       * In this layout, we remove the image from all items except the second one.
       */
      case SERVICES_LAYOUTS.LayoutTwo:
        return items.map((item, index) => {
          const removeImage = index !== 1;
          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      /**
       * In this layout, we remove the image from all items.
       */
      case SERVICES_LAYOUTS.LayoutThree:
        return items.map((item) => {
          return {
            ...omit(item, ["image"]),
          };
        });
      /**
       * In this layout, we remove the image from all items.
       */
      case SERVICES_LAYOUTS.LayoutFour:
        return items.map((item, index) => {
          const removeImage = index !== 0;

          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      case SERVICES_LAYOUTS.LayoutFive:
        return items.map((item) => {
          return {
            ...omit(item, ["image"]),
          };
        });
      default:
        return items;
    }
  }, [items, layout]);

  return (
    <Section className={clsx("base-col ui-service-items-section", className)}>
      <Col className="ui-service-items-sectirron-content">
        <SectionHeading
          fs={"2"}
          mdFs={"2"}
          className="ui-service-items-section-title"
          titleMarginBottom="mb-x0"
          descriptionMarginBottom="mb-x0"
          color={"slate-20"}
          title={title}
          description={<RichText description={description}/>}
        >
          {goToLabel && href && (
            <ArrowLink href={href} asLink={asLink} fs="7">
              {goToLabel.toUpperCase()}
            </ArrowLink>
          )}
        </SectionHeading>
      </Col>
      <ul
        role="list"
        aria-label={`Lista di ${title}`}
        className={`list-style-none ${layoutClassName}`}
      >
        {itemsNormalized?.map((item, index) => {
          return (
            <li key={[item._id, index].join("-")}>
              <CardCategory
                key={[item._id, index].join("-")}
                title={item?.name}
                description={item?.description}
                image={item?.image}
                href={item?.link || item?.externalLink}
                showIcon={item?.externalLink}
                asLink={asLink}
                isExternal={item?.externalLink}
              />
            </li>
          );
        })}
      </ul>
    </Section>
  );
};
